import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, Check, CheckCheck, Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const rel = (iso) => {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es }); }
  catch { return ""; }
};

export default function NotificationsBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["zc-notifications"],
    queryFn: async () => (await api.get("/zicasso/notifications")).data,
    refetchInterval: 60_000,
  });

  const readMut = useMutation({
    mutationFn: (id) => api.post(`/zicasso/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["zc-notifications"] }),
  });
  const readAllMut = useMutation({
    mutationFn: () => api.post("/zicasso/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["zc-notifications"] }),
  });

  const unread = data?.unread_count || 0;
  const items = data?.items || [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative flex h-9 w-9 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          data-testid="notifications-bell"
          aria-label="Notificaciones"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#C45A45] px-1 text-[9px] font-bold text-white"
              data-testid="notifications-unread-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" side="right" className="w-80 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b px-3 py-2.5">
          <span className="text-sm font-semibold">Notificaciones</span>
          {unread > 0 && (
            <button
              onClick={() => readAllMut.mutate()}
              className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
              data-testid="notifications-read-all"
            >
              <CheckCheck className="h-3 w-3" /> Marcar todas
            </button>
          )}
        </div>
        <div className="thin-scroll max-h-[360px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              <Bell className="mx-auto mb-2 h-6 w-6 opacity-30" />
              Sin notificaciones
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => !n.read && readMut.mutate(n.id)}
                className={`flex w-full items-start gap-2 border-b px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-accent ${n.read ? "opacity-60" : ""}`}
                data-testid={`notification-${n.id}`}
              >
                <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${n.overdue ? "bg-[#FCEEEA] text-[#C45A45]" : "bg-secondary text-foreground"}`}>
                  <Clock className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold leading-tight">{n.title}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">{n.body}</div>
                  <div className="mt-1 text-[10px] text-muted-foreground/70">{rel(n.created_at)}</div>
                </div>
                {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#C45A45]" />}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
